import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Home, Phone, Mail, ArrowRight, Sparkles, Star, Clock, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function ThankYou() {
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
        {/* Animated Background Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-pink-500/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 right-10 w-64 h-64 bg-blue-500/15 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
        
        <div className="container mx-auto px-4 max-w-3xl py-20 relative z-10">
          {/* Success Card with Glassmorphism */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header Section */}
            <div className="text-center pt-12 pb-8 px-8">
              {/* Animated Success Icon */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full blur-xl opacity-60 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 p-5 rounded-full shadow-lg shadow-cyan-500/30">
                    <CheckCircle2 className="w-14 h-14 text-white" data-testid="icon-success" />
                  </div>
                  <div className="absolute -top-1 -right-1">
                    <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
                  </div>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent" data-testid="heading-thank-you">
                Thank You!
              </h1>
              <p className="text-lg text-white/70 max-w-lg mx-auto">
                We've received your information and appreciate you reaching out. A member of our team will be in touch with you shortly.
              </p>
            </div>
            
            {/* What Happens Next Section */}
            <div className="px-8 pb-8">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 via-purple-500/30 to-pink-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#16162a] border border-white/10 p-8 rounded-2xl text-center">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-cyan-500/30 rounded-full blur-lg animate-pulse"></div>
                      <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-400/30 flex items-center justify-center">
                        <MessageCircle className="w-10 h-10 text-cyan-400" />
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    What Happens Next?
                  </h3>
                  <p className="text-white/60 mb-6 max-w-md mx-auto">
                    Our team will review your information and reach out within 1-2 business days to discuss how we can help your business grow online.
                  </p>
                  
                  <p className="text-white/40 text-sm flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    We're excited to learn more about your business!
                  </p>
                </div>
              </div>
            </div>

            {/* What to Expect Section */}
            <div className="px-8 pb-8">
              <div className="bg-white/5 border border-white/10 p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-400/30 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-400" />
                  </div>
                  <h4 className="font-bold text-white text-lg">When We Connect, We'll Discuss:</h4>
                </div>
                
                <ul className="space-y-4">
                  {[
                    "Your current online presence and goals",
                    "What's working and what could be improved",
                    "How we can help more people find your business",
                    "Next steps that make sense for your situation"
                  ].map((item, index) => (
                    <li key={index} className="flex gap-3 items-start">
                      <div className="mt-0.5">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                          <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        </div>
                      </div>
                      <span className="text-white/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Direct Contact Section */}
            <div className="px-8 pb-8">
              <div className="border-t border-white/10 pt-6">
                <h3 className="font-semibold text-white mb-4">Need to Reach Us Sooner?</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white mb-1">Call Us</p>
                        <a 
                          href="tel:+18667561020" 
                          className="text-white/60 hover:text-cyan-400 transition-colors"
                          data-testid="link-phone"
                        >
                          (866) 756-1020
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-400/30 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white mb-1">Email Us</p>
                        <a 
                          href="mailto:sales@integradyn.ai" 
                          className="text-white/60 hover:text-purple-400 transition-colors"
                          data-testid="link-email"
                        >
                          sales@integradyn.ai
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="px-8 pb-10">
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/" className="flex-1">
                  <Button 
                    variant="outline" 
                    className="w-full gap-2 bg-transparent border-white/20 text-white" 
                    data-testid="button-home"
                  >
                    <Home className="w-4 h-4" />
                    Back to Homepage
                  </Button>
                </Link>
                <Link href="/services" className="flex-1">
                  <Button 
                    className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold" 
                    data-testid="button-services"
                  >
                    Explore Our Services
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
